import { Card, CardContent } from '@/components/ui/card';
import { usePayrollSubscription, usePayrollWeek } from '@/hooks/usePayrollWeek';
import { memo, useMemo } from 'react';
import { WeeklyPerformanceChart } from '../WeeklyPerformanceChart/WeeklyPerformanceChart';
import DayItem from './components/DayItem';
import WeeklyHeader from './components/WeeklyHeader';
import { getWeekRange } from './utils';

interface SalonWeeklyProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

// Loading skeleton component
const LoadingSkeleton = () => (
  <Card>
    <CardContent className="p-6">
      <div className="space-y-4">
        <div className="h-4 animate-pulse bg-muted rounded" />
        <div className="h-32 animate-pulse bg-muted rounded" />
      </div>
    </CardContent>
  </Card>
);

// Main SalonWeekly component - Displays weekly payroll summary
const SalonWeekly = ({ selectedDate, onDateChange }: SalonWeeklyProps) => {
  const { data: payrollData, isLoading, isFetching } = usePayrollWeek(selectedDate);
  usePayrollSubscription(() => {});

  // Calculate week range and memoize it
  const weekRange = useMemo(() => getWeekRange(selectedDate), [selectedDate]);
  // Loading state — only on first load so the date picker is not unmounted
  if (isLoading && !payrollData) {
    return <LoadingSkeleton />;
  }

  if (!payrollData) {
    return <LoadingSkeleton />;
  }

  const maxIncome = Math.max(...payrollData.records.map(day => day.income));

  return (
    <Card className="border-none">
      <CardContent className={`p-2 py-6 sm:p-6${isFetching ? ' opacity-90' : ''}`}>
        <WeeklyHeader
          totalIncome={payrollData.totalIncome}
          weekRange={weekRange}
          date={selectedDate}
          onDateChange={onDateChange}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Daily Records - Takes 2/3 of space */}
          <div className="md:col-span-2 space-y-2">
            {payrollData.records.map(day => (
              <DayItem key={day.fullDate} day={day} maxIncome={maxIncome} />
            ))}
          </div>

          {/* Weekly Performance Chart - Takes 1/3 of space */}
          <div className="md:col-span-1">
            <WeeklyPerformanceChart data={payrollData.records} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default memo(SalonWeekly);
